import {
  ContactCandidates,
  ContactEnrichments,
} from "../models/peopleCandidate";
import { HubSpotMatches } from "../models/hubspotMatch";
import axiosConfig from "./axiosConfig";

// The operational InRange site the operator anchored the search on when this lead
// was promoted, snapshotted at promote time (so a later search elsewhere can't change
// it). Drives the "near <site>" chip on the leads panes and the cold-email proximity
// hook. null when the lead wasn't promoted from a "search near a site".
export interface ProximityContext {
  anchorSiteId: string;
  anchorSiteName: string | null;
  distanceMetres: number | null;
  operationalAtCapture: boolean;
  capturedBy: string | null;
  capturedAt: string | null;
}

export interface Lead {
  id: string;
  source: string;
  sourceRef: string;
  sourcePayload: Record<string, unknown> | null;
  // Property info
  address: string | null;
  postcode: string | null;
  postcodeOutward: string | null;
  latitude: number | null;
  longitude: number | null;
  floorAreaSqm: number | null;
  declaredNumBuildings: number | null;
  occupierName: string | null;
  // Per-source enrichment (always populated for CSV-imported leads;
  // copied from the VOA row at promotion time for VOA-promoted leads)
  polygonData: Record<string, unknown> | null;
  hmlrData: Record<string, unknown> | null;
  tenantData: Record<string, unknown> | null;
  // Agentic landlord-research candidate pool ({matches, researchedAt, from}),
  // copied from the VOA row at promotion. See AgenticMatch for the item shape.
  agenticData: Record<string, unknown> | null;
  // The operationKey of a landlord-research run (single or bulk) that is LIVE for this
  // row right now, else null. Server-derived per response, so any surface rendering the
  // row shows the true state: the launching page's in-memory handle is lost by
  // navigating away, searching another area, or opening a second tab.
  researchOperationKey: string | null;
  // Operator decisions (atomic via PATCH /leads/<id>/decisions)
  polygonDecision: Record<string, unknown> | null;
  landlordDecision: Record<string, unknown> | null;
  tenantDecision: Record<string, unknown> | null;
  // Step-5 people-discovery result keyed by role ("landlord"/"tenant"); null
  // until a "Find people" run has happened. Rendered on the lead panel.
  contactCandidates: ContactCandidates | null;
  // Step-7 paid contact enrichments keyed by role; null until a "Pull contact
  // info" run has happened. Separate from contactCandidates so a re-discovery
  // can't wipe paid reveals. Rendered in the per-role Contact details tile.
  contactEnrichments: ContactEnrichments | null;
  // What HubSpot holds for this lead's landlord / tenant company, keyed by role.
  hubspotMatches: HubSpotMatches | null;
  // The operational site anchored on when this lead was promoted from a "search near
  // a site"; null otherwise. Rendered as a "near <site>" chip on the leads panes.
  proximityContext: ProximityContext | null;
  ownerOccupier: boolean;
  // Operator-flagged: this building already carries a solar PV installation.
  // False means "not flagged", never "no solar" - nothing infers an absence from
  // it. The outreach drafter uses the EFFECTIVE value (this OR the linked site's
  // own declared existing capacity, see `siteExistingPvKwpByLeadId`), not this alone.
  hasPv: boolean;
  tags: string[];
  // Dedupe
  duplicateOfLeadID: string | null;
  matchedExistingSiteID: string | null;
  createdAt: string;
  updatedAt: string;
  // Server-computed: minimum of polygon + landlord + tenant per-element
  // confidences (0-100). null when any required decision is missing -
  // treat as "incomplete" for site-creation purposes, not "low".
  leadConfidence: number | null;
}

export interface LeadListResponse {
  leads: Lead[];
  // Backlinks for each lead to any sites whose `originatedFromLeadId`
  // matches it. Lets the leads table render a [Site] button on rows
  // that already have a site created from them. Empty entries are
  // omitted from the map.
  siteIdsByLeadId: Record<string, string[]>;
  // Total building area (m²) of each lead's *linked* site (the site it
  // created, else its `matchedExistingSiteID`), so the table can show the
  // site's real footprint instead of the lead's VOA/OSM candidate area.
  // Leads with no linked site (or an unresolvable one) are omitted.
  siteBuildingAreaByLeadId: Record<string, number>;
  // How many distinct contacts an outreach email has been drafted for, per lead.
  // Threads, not reruns - a rerun of the same contact's email does not increment it.
  // Leads with no drafts are omitted. A proxy for "how many have been sent" until
  // the send integration lands.
  outreachContactCountByLeadId: Record<string, number>;
  // Existing solar (kWp) declared on each lead's *linked* site's Demand tab, present
  // only when non-zero. The derived half of `Lead.hasPv`: a lead here has existing
  // solar whatever its own flag says, so the panel renders the toggle on and
  // read-only. The outreach drafter derives the same signal from the same site
  // field, so the two surfaces agree by construction.
  siteExistingPvKwpByLeadId: Record<string, number>;
}

const getLeads = async (authToken: string): Promise<LeadListResponse> => {
  const { data } = await axiosConfig.get(`/leads`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};

export default getLeads;
